/**
 * Selector de variante con la temática de la tienda (Optioutlet).
 *
 * El <select> nativo se puede pintar cerrado, pero la lista que despliega
 * la dibuja el sistema: sale blanca y azul, igual que pasaba con el
 * <input type="date">. Aquí el select se queda en el formulario -- oculto
 * a la vista pero vivo -- y encima se monta una lista propia.
 *
 * Al elegir se escribe el valor en el select y se dispara un 'change' que
 * burbujea, que es lo que escucha variant-picker.js del tema. Así la
 * lógica de variantes sigue siendo la suya y no se duplica nada.
 */
const FLECHA = `<svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.4" d="m5.5 8 4.5 4.5L14.5 8"/></svg>`;

function montar(select) {
  if (select.dataset.olMontado) return;
  select.dataset.olMontado = 'si';

  const caja = document.createElement('div');
  caja.className = 'ol-select';

  const boton = document.createElement('button');
  boton.type = 'button';
  boton.className = 'ol-select__campo';
  boton.setAttribute('aria-haspopup', 'listbox');
  boton.setAttribute('aria-expanded', 'false');
  const etiqueta = select.closest('.variant-option')?.querySelector('label');
  if (etiqueta?.id) boton.setAttribute('aria-labelledby', etiqueta.id);
  else if (etiqueta) boton.setAttribute('aria-label', etiqueta.textContent.trim());

  const valor = document.createElement('span');
  valor.className = 'ol-select__valor';
  const flecha = document.createElement('span');
  flecha.className = 'ol-select__flecha';
  flecha.innerHTML = FLECHA;
  boton.append(valor, flecha);

  const lista = document.createElement('ul');
  lista.className = 'ol-select__lista';
  lista.setAttribute('role', 'listbox');
  lista.hidden = true;

  const pintarValor = () => {
    valor.textContent = select.selectedOptions[0]?.textContent.trim() ?? '';
  };

  const opciones = [...select.options].map((o, i) => {
    const li = document.createElement('li');
    li.className = 'ol-select__opcion';
    li.setAttribute('role', 'option');
    li.tabIndex = -1;
    li.textContent = o.textContent.trim();
    li.dataset.indice = String(i);
    if (o.disabled) li.setAttribute('aria-disabled', 'true');
    li.addEventListener('click', () => {
      if (o.disabled) return;
      select.selectedIndex = i;
      // El tema escucha este evento en <variant-picker>; tiene que burbujear.
      select.dispatchEvent(new Event('change', { bubbles: true }));
      pintarValor();
      marcar();
      cerrar();
    });
    lista.append(li);
    return li;
  });

  const marcar = () => {
    opciones.forEach((li, i) => {
      const puesto = i === select.selectedIndex;
      li.classList.toggle('ol-select__opcion--elegida', puesto);
      li.setAttribute('aria-selected', puesto ? 'true' : 'false');
    });
  };

  function abrir() {
    lista.hidden = false;
    boton.setAttribute('aria-expanded', 'true');
    const puesta = opciones[select.selectedIndex] ?? opciones[0];
    puesta?.focus();
    // Que la elegida quede a la vista sin arrastrar la página.
    if (puesta) lista.scrollTop = puesta.offsetTop - lista.clientHeight / 2 + puesta.offsetHeight / 2;
  }

  function cerrar({ devolverFoco = true } = {}) {
    if (lista.hidden) return;
    lista.hidden = true;
    boton.setAttribute('aria-expanded', 'false');
    if (devolverFoco) boton.focus();
  }

  boton.addEventListener('click', () => (lista.hidden ? abrir() : cerrar()));

  lista.addEventListener('keydown', (e) => {
    const i = opciones.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.stopPropagation(); cerrar(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); opciones[Math.min(i + 1, opciones.length - 1)]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); opciones[Math.max(i - 1, 0)]?.focus(); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.activeElement?.click(); }
  });

  document.addEventListener('click', (e) => {
    if (!lista.hidden && !caja.contains(e.target)) cerrar({ devolverFoco: false });
  });

  // Si el tema cambia el select por su cuenta, el botón lo refleja.
  select.addEventListener('change', () => { pintarValor(); marcar(); });

  // El envoltorio del tema recorta con overflow:clip y trae su propia
  // flecha. Se marca para desactivarle las dos cosas desde el CSS.
  select.parentElement.classList.add('ol-select-envoltorio');
  select.closest('.variant-option')?.classList.add('ol-variant-option');
  select.parentElement.insertBefore(caja, select);
  caja.append(boton, lista, select);
  pintarValor();
  marcar();
}

const montarTodos = () =>
  document.querySelectorAll('.variant-option__select:not([data-ol-montado])').forEach(montar);

montarTodos();

// variant-picker.js vuelve a pintar su HTML cuando cambia la variante, asi
// que hay que re-montar lo que llegue nuevo.
new MutationObserver(montarTodos).observe(document.body, { childList: true, subtree: true });
